import { useEffect, useState, useCallback } from "react";
import { Trash2 } from "lucide-react";
import apiClient from "../api/apiClient";
import DataTable from "../components/common/DataTable";
import SearchBar from "../components/common/SearchBar";
import PageTitle from "../components/common/PageTitle";
import AddButton from "../components/common/AddButton";
import AddItemModal from "../components/modals/AddItemModal";
import { confirmDelete } from "../utils/confirm";
import { toastCreate, toastDelete, showError, showSuccess } from "../utils/toast";

export default function InovasiList() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [projects, setProjects] = useState([]);

  // Modal State
  const [showAddPopup, setShowAddPopup] = useState(false);

  // Form State
  const [projectId, setProjectId] = useState("");
  const [judulMakalah, setJudulMakalah] = useState("");
  const [tanggalRencana, setTanggalRencana] = useState("");
  const [tanggalAktual, setTanggalAktual] = useState("");

  // ================= FETCH DATA INOVASI =================
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Pastikan endpoint di backend Anda adalah /api/monitoring-inovasi
      // apiClient biasanya sudah secara otomatis menambahkan prefix /api
      const res = await apiClient.get(`/monitoring-inovasi`);
      setData(res.data || []);
    } catch (err) {
      showError("Gagal memuat data inovasi");
    } finally {
      setLoading(false);
    }
  }, []);

  // ================= FETCH PROJECTS =================
  const fetchProjects = async () => {
    try {
      const res = await apiClient.get(`/get-all-projects`);
      setProjects(res.data || []);
    } catch {
      showError("Gagal memuat daftar project");
    }
  };

  useEffect(() => {
    fetchData();
    fetchProjects();
  }, [fetchData]);

  // ================= ADD DATA INOVASI =================
  const handleAddInovasi = async () => {
    if (!projectId || !judulMakalah || !tanggalRencana) {
      showError("Mohon lengkapi project, judul, dan tanggal rencana!");
      return;
    }

    try {
      const payload = {
        project_id: projectId,
        judul_makalah: judulMakalah,
        tanggal_rencana_selesai: tanggalRencana,
        tanggal_aktual_selesai: tanggalAktual || null,
      };

      await toastCreate(apiClient.post(`/monitoring-inovasi`, payload), judulMakalah);
      
      // Reset form & tutup modal
      setProjectId("");
      setJudulMakalah("");
      setTanggalRencana("");
      setTanggalAktual("");
      setShowAddPopup(false);
      
      fetchData(); // Reload data
    } catch (err) {
      showError(err.response?.data?.error || "Gagal menambah data inovasi");
    }
  };

  // ================= UPDATE TANGGAL AKTUAL =================
  const handleUpdateAktual = async (id, newDate) => {
    if (!newDate) return;
    try {
      await apiClient.put(`/monitoring-inovasi/${id}/aktual`, {
        tanggal_aktual_selesai: newDate,
      });
      showSuccess("Tanggal aktual berhasil disimpan");
      fetchData(); // Reload table untuk update status
    } catch {
      showError("Gagal menyimpan tanggal aktual");
    }
  };

  // ================= DELETE DATA =================
  const handleDelete = (id, judul) => {
    confirmDelete(judul, async () => {
      await toastDelete(apiClient.delete(`/monitoring-inovasi/${id}`), judul);
      fetchData();
    });
  };

  // ================= UTILS: STATUS & DATE FORMAT =================
  const checkStatus = (rencana, aktual) => {
    if (aktual) return { text: "Selesai", color: "text-green-600 bg-green-50 border-green-200" };
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const rencanaDate = new Date(rencana);

    if (rencanaDate < today) {
      return { text: "Terlambat", color: "text-red-600 bg-red-50 border-red-200" };
    }
    return { text: "On Progress", color: "text-blue-600 bg-blue-50 border-blue-200" };
  };

  const formatDateUI = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric", month: "short", year: "numeric",
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    return dateString.split("T")[0];
  };

  // ================= FILTER DATA =================
  const filteredData = (data || []).filter((item) =>
    ((item.project_name || "") + (item.judul_makalah || ""))
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  if (loading) return <p className="text-center py-10">Loading...</p>;

  return (
    <div className="p-6 pt-20">
      {/* HEADER */}
      <div className="flex justify-between mb-5">
        <PageTitle subtitle="Monitoring progres Karya Inovasi proyek">
          Daftar Karya Inovasi
        </PageTitle>
        <AddButton onClick={() => setShowAddPopup(true)} />
      </div>

      {/* SEARCH */}
      <div className="mb-4 space-y-2">
        <SearchBar
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Cari nama project atau judul makalah..."
        />
        <p className="text-gray-500 text-sm">
          {search
            ? `Hasil pencarian: ${filteredData.length} data`
            : `Total data: ${data.length}`}
        </p>
      </div>

      {/* TABLE */}
      <DataTable
        columns={[
          { field: "index", label: "No", className: "w-[60px] text-center" },
          { field: "project", label: "Nama Proyek" },
          { field: "judul", label: "Judul Makalah" },
          { field: "rencana", label: "Rencana Selesai", className: "w-[150px]" },
          { field: "aktual", label: "Aktual Selesai", className: "w-[160px]" },
          { field: "status", label: "Status", className: "w-[120px] text-center" },
          { field: "aksi", label: "Aksi", className: "w-[80px] text-center" },
        ]}
        data={filteredData}
        renderRow={(item, index) => {
          const status = checkStatus(item.tanggal_rencana_selesai, item.tanggal_aktual_selesai);
          
          return (
            <tr key={item.id} className="hover:bg-gray-50/50 transition-colors border-b border-gray-100">
              <td className="py-3 px-4 text-center text-sm text-gray-500">{index + 1}</td>
              <td className="py-3 px-4 text-sm font-medium text-gray-900">{item.project_name}</td>
              <td className="py-3 px-4 text-sm text-gray-700">{item.judul_makalah}</td>
              <td className="py-3 px-4 text-sm text-gray-600">{formatDateUI(item.tanggal_rencana_selesai)}</td>
              <td className="py-3 px-4">
                <input
                  type="date"
                  defaultValue={formatDateForInput(item.tanggal_aktual_selesai)}
                  onChange={(e) => handleUpdateAktual(item.id, e.target.value)}
                  className="px-2 py-1.5 border border-gray-300 rounded text-sm outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-full"
                />
              </td>
              <td className="py-3 px-4 text-center">
                <span className={`inline-flex px-2 py-1 text-xs rounded-full border font-medium ${status.color}`}>
                  {status.text}
                </span>
              </td>
              <td className="py-3 px-4 text-center">
                <button
                  onClick={() => handleDelete(item.id, item.judul_makalah)}
                  className="text-gray-400 hover:text-red-500 transition-colors p-1"
                  title="Hapus Data"
                >
                  <Trash2 size={18} />
                </button>
              </td>
            </tr>
          );
        }}
      />

      {/* ================= ADD INOVASI MODAL ================= */}
      <AddItemModal
        isOpen={showAddPopup}
        onClose={() => setShowAddPopup(false)}
        onSubmit={handleAddInovasi}
        title="Tambah Progres Inovasi"
        submitLabel="Simpan Data"
      >
        <div className="space-y-4 text-sm">
          <div>
            <label className="block text-gray-700 mb-1 font-medium">Pilih Project <span className="text-red-500">*</span></label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-blue-500"
            >
              <option value="">-- Pilih Project --</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-gray-700 mb-1 font-medium">Judul Makalah <span className="text-red-500">*</span></label>
            <input 
              placeholder="Contoh: Optimalisasi Penggunaan Alat..." 
              value={judulMakalah} 
              onChange={(e) => setJudulMakalah(e.target.value)} 
              className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-blue-500" 
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-1 font-medium">Tanggal Rencana Selesai <span className="text-red-500">*</span></label>
            <input 
              type="date" 
              value={tanggalRencana} 
              onChange={(e) => setTanggalRencana(e.target.value)} 
              className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-blue-500" 
            />
          </div>

          <div>
            <label className="block text-gray-700 mb-1 font-medium">Tanggal Aktual Selesai <span className="text-gray-400 font-normal">(Opsional)</span></label>
            <input 
              type="date" 
              value={tanggalAktual} 
              onChange={(e) => setTanggalAktual(e.target.value)} 
              className="w-full border border-gray-300 rounded px-3 py-2 outline-none focus:border-blue-500" 
            />
          </div>
        </div>
      </AddItemModal>
    </div>
  );
}
